/**
 * Onboarding Service — client-side thin wrapper.
 *
 * All heavy work (Supabase cache + OpenAI generation) runs in the secure
 * server-side Route Handler at /api/onboarding-path so the OpenAI API key
 * is never exposed to the browser bundle.
 *
 * Cache model:
 *   A) Cache hit  → Supabase returns existing path_data  (OpenAI cost = $0)
 *   B) Cache miss → OpenAI generates path, server saves to Supabase, returns result
 */

function normalizeGoal(userGoal) {
  return String(userGoal || '').trim().toLowerCase();
}

function normalizeLocale(locale) {
  return String(locale || 'es').trim().toLowerCase();
}

/**
 * Fetches the onboarding path for a given user goal.
 * Throws with a descriptive message on any failure so callers
 * (e.g. GlobalOnboardingEntry) can surface the error to the user.
 *
 * @param {string} userGoal  The user's health goal (e.g. "perder peso", "general").
 * @param {string} locale    Current UI locale (e.g. "es", "en").
 * @returns {Promise<{ version: number, steps: Array }>}
 */
export async function getOnboardingPath(userGoal, locale = 'es') {
  const normalizedGoal = normalizeGoal(userGoal);
  const normalizedLocale = normalizeLocale(locale);

  if (!normalizedGoal) {
    throw new Error('getOnboardingPath requires a non-empty userGoal');
  }

  const url = `/api/onboarding-path?goal=${encodeURIComponent(normalizedGoal)}&lang=${encodeURIComponent(normalizedLocale)}`;

  let response;
  try {
    response = await fetch(url, { method: 'GET' });
  } catch (networkError) {
    throw new Error(`Network error fetching onboarding path: ${networkError.message}`);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody?.error || `Onboarding path request failed (HTTP ${response.status})`
    );
  }

  const path = await response.json();
  return path;
}

export default { getOnboardingPath };



